import { OvFormInput } from '@components'
import { ENDPOINTS, HELPER_TEXT, LABELS, PLACEHOLDERS, TOOLTIPS } from '@constants'
import { useStore } from '@hooks/useStore'
import { Alert, Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { passwordChangeValidationSchema } from './validation/password-change.form.validation'

interface PasswordChangeData {
  current_password: string
  new_password: string
  new_password_confirmation: string
}

export const PasswordChangeForm = observer(() => {
  const { api } = useStore()
  const [successMessage, setSuccessMessage] = useState(false)

  const form = useForm<PasswordChangeData>({
    resolver: yupResolver(passwordChangeValidationSchema()),
    defaultValues: {
      current_password: '',
      new_password: '',
      new_password_confirmation: '',
    },
    mode: 'onChange',
  })

  const handleCancel = () => {
    form.reset()
    setSuccessMessage(false)
  }

  const onSubmit = async (data: PasswordChangeData) => {
    setSuccessMessage(false)
    try {
      await api.instance.api.post(ENDPOINTS.USER.CHANGE_PASSWORD, data)
      toast.success('Wachtwoord succesvol gewijzigd')
      setSuccessMessage(true)
      form.reset()
    } catch (error: any) {
      const errorMessage = error?.message || 'Er is een fout opgetreden'
      toast.error(errorMessage)

      // Set form errors if validation errors exist
      if (error?.errors) {
        const errors = error.errors
        Object.keys(errors).forEach((key) => {
          form.setError(key as keyof PasswordChangeData, {
            type: 'server',
            message: errors[key][0],
          })
        })
      }
    }
  }

  const FormInput = OvFormInput<PasswordChangeData>

  return (
    <OvFormProvider
      form={form}
      onSubmit={() => form.handleSubmit(onSubmit)()}
      onCancel={handleCancel}
      loading={false}
      enableNavigationBlocker={false}>
      <Paper sx={{ p: 3 }}>
        <Stack
          direction="column"
          gap={3}>
          <Typography variant="h4">Wachtwoord wijzigen</Typography>

          {successMessage && (
            <Alert
              severity="success"
              onClose={() => setSuccessMessage(false)}>
              Je wachtwoord is succesvol gewijzigd. Alle andere sessies zijn automatisch uitgelogd voor
              je beveiliging.
            </Alert>
          )}

          <Alert severity="info">
            <Typography
              variant="body2"
              gutterBottom>
              Wachtwoord vereisten:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Minimaal 8 tekens</li>
              <li>Minimaal één hoofdletter en één kleine letter</li>
              <li>Minimaal één cijfer</li>
            </ul>
          </Alert>

          <OvForm>
            <Stack
              direction="column"
              gap={2}>
              <FormInput
                label="Huidig wachtwoord"
                name="current_password"
                type="password"
                placeholder={PLACEHOLDERS.PASSWORD}
                helperText={HELPER_TEXT.PASSWORD_CURRENT}
              />

              <FormInput
                label="Nieuw wachtwoord"
                name="new_password"
                type="password"
                placeholder={PLACEHOLDERS.PASSWORD}
                helperText={HELPER_TEXT.PASSWORD_REQUIREMENTS}
              />

              <FormInput
                label="Bevestig nieuw wachtwoord"
                name="new_password_confirmation"
                type="password"
                placeholder={PLACEHOLDERS.PASSWORD}
                helperText={HELPER_TEXT.PASSWORD_CONFIRM}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Tooltip title={TOOLTIPS.CANCEL_ESC}>
                  <span>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={!form.formState.isDirty}
                      size="small">
                      {LABELS.CANCEL}
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={TOOLTIPS.CHANGE_PASSWORD}>
                  <span>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={!form.formState.isDirty || form.formState.isSubmitting}
                      size="small">
                      {LABELS.PASSWORD_CHANGE}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Stack>
          </OvForm>
        </Stack>
      </Paper>
    </OvFormProvider>
  )
})
